// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React from 'react';
import { Image, IStackStyles, ITextStyles, Stack, Text } from '@fluentui/react';
import { useRerenderAtIntervals } from 'hooks/useRerenderAtIntervals';
import { useBranding } from 'theme/SwitchableThemeProvider';

// This is used to avoid reflow when the page is first loading.
// This should be the desired size of the final image when it is loaded.
// API requires this to be pixels and not rem.
const DEFAULT_HERO_IMAGE_HEIGHT_PX = 120;

export const Welcome = (props: { isSignedIn?: boolean; name?: string }): JSX.Element => {
  // Rerender at the start of every hour to update the welcome message.
  // For simplicity we will just rerender at the start of every hour.
  // A more optimized technique would be to only rerender at known times
  // when the message will change.
  const timeUntilNextHourInMilliseconds = (60 - new Date().getMinutes()) * 60 * 1000;
  const oneHourInMilliseconds = 1000 * 60 * 60;
  useRerenderAtIntervals(timeUntilNextHourInMilliseconds, oneHourInMilliseconds);

  const branding = useBranding();
  const message = props.isSignedIn
    ? `${getMorningAfternoonEveningOrNightMessage(branding.welcomeMessages)}, ${
        props.name?.split(' ')[0] ?? 'Unnamed Participant'
      }.`
    : 'Welcome to ACS Selfhost. Sign in to get started.';

  return (
    <Stack
      styles={welcomeContainerStyles}
      verticalAlign={props.isSignedIn ? undefined : 'center'}
      tokens={{ childrenGap: '1.25rem' }}
      horizontalAlign="center"
    >
      <Stack.Item>
        <Image
          height={DEFAULT_HERO_IMAGE_HEIGHT_PX} /* Define a height to prevent UI reflow when the image loads */
          src={branding.catImage}
          alt="A cat wearing headphones"
          role="presentation"
        />
      </Stack.Item>
      <Stack.Item>
        <Text styles={welcomeTextStyles}>{message}</Text>
      </Stack.Item>
    </Stack>
  );
};

const getMorningAfternoonEveningOrNightMessage = (welcomeMessages: {
  morning: string;
  afternoon: string;
  evening: string;
  night: string;
}): string => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) {
    return welcomeMessages.morning;
  } else if (hour < 18) {
    return welcomeMessages.afternoon;
  } else if (hour < 22) {
    return welcomeMessages.evening;
  } else {
    return welcomeMessages.night;
  }
};

const welcomeTextStyles: ITextStyles = {
  root: {
    fontWeight: '600',
    fontSize: '1.25rem',
    lineHeight: '1.75rem'
  }
};

const welcomeContainerStyles: IStackStyles = {
  root: {
    paddingTop: '1.5rem',
    paddingBottom: '1.5rem',
    '@media only screen and (max-width: 600px)': {
      textAlign: 'center',
      paddingRight: '2rem',
      paddingLeft: '2rem'
    }
  }
};
